import { cx } from '@twind/core';
import { useState, useEffect, PropsWithChildren, createRef, RefObject, useLayoutEffect } from 'react';

type CustomScrollbarProps = PropsWithChildren<{
  id?: string;
  dataTestId?: string;
  className?: string;
  alignToBottom?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  rememberScrollPosition?: boolean;
  role?: string;
  tabIndex?: number;
  'aria-label'?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
  'aria-relevant'?:
    | 'text'
    | 'additions'
    | 'additions removals'
    | 'additions text'
    | 'all'
    | 'removals'
    | 'removals additions'
    | 'removals text'
    | 'text additions'
    | 'text removals';
}>;

const positions = new Map<string, number>();

const CustomScrollbar = ({
  id,
  dataTestId,
  children,
  className,
  alignToBottom = false,
  containerRef = createRef(),
  rememberScrollPosition = false,
  role,
  tabIndex,
  'aria-label': ariaLabel,
  'aria-live': ariaLive,
  'aria-relevant': ariaRelevant,
}: CustomScrollbarProps) => {
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleScroll = () => {
    if (containerRef.current) {
      setIsScrolling(true);
      setLastScrollTime(Date.now());

      const { scrollTop } = containerRef.current;

      if (rememberScrollPosition && id) {
        positions.set(id, scrollTop);
      }
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isScrolling) {
      timeout = setTimeout(() => {
        if (Date.now() - lastScrollTime > 500) {
          setIsScrolling(false);
        }
      }, 500);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isScrolling, lastScrollTime]);

  useEffect(() => {
    const contentElement = containerRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (rememberScrollPosition) {
      const contentElement = containerRef.current;
      if (contentElement && id) {
        const position = positions.get(id);
        if (position) {
          contentElement.scrollTop = position;
        }
      }
    }
  }, [rememberScrollPosition, id, containerRef]);

  return (
    <section
      role="region"
      aria-label="Scrollable content"
      id={id}
      data-testid={dataTestId}
      className="relative w-full h-0 grow overflow-hidden flex flex-col"
    >
      <div
        id="custom-scrollbar"
        ref={containerRef}
        className={cx(
          `~(absolute ${
            alignToBottom ? 'bottom-0 max-h-full' : 'top-0 h-full'
          } grow left-0 right-0 overflow-y-scroll md:custom-scrollbar-content focus:outline-none)`,
          className
        )}
        onScroll={handleScroll}
        role={role}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-live={ariaLive}
        aria-relevant={ariaRelevant}
      >
        {children}
      </div>
    </section>
  );
};

CustomScrollbar.displayName = 'CustomScrollbarV2';

export default CustomScrollbar;
